import React, { useState } from "react";
import {
  Page, PageHeader, Card, Button, Field, TextInput, Callout, SegmentedControl, Dialog,
} from "../../../ui";
import "./classCorrection.scss";
import {
  ClassCorrectionAction,
  ClassCorrectionResolved,
  runClassCorrection,
} from "../../../Api/apiUtils";
import { CLASSOPS_BASEURL } from "../../../Api/api-constant";

interface FormState {
  teacherId: string;
  slotDate: string;
  startTime: string;
  studentMobile: string;
}

interface Outcome {
  ok: boolean;
  title: string;
  message: string;
  hint?: string;
  candidates?: Record<string, (string | number)[]>;
}

// a preview is only good for the exact inputs it was run with
interface Preview {
  key: string;
  resolved: ClassCorrectionResolved;
}

const EMPTY: FormState = {
  teacherId: "",
  slotDate: "",
  startTime: "",
  studentMobile: "",
};

interface ActionCopy {
  label: string;
  description: string;
  changes: string[];
  readyMessage: string;
  confirmTitle: string;
  notFoundHint: string;
}

// the two corrections differ in where the teacher slot comes from: /complete
// attaches a free one, /mark-completed keeps the one the class already has
const ACTIONS: Record<ClassCorrectionAction, ActionCopy> = {
  complete: {
    label: "Complete class",
    description:
      "The class has no teacher slot attached: binds the teacher's free slot at that date and " +
      "time to the student's class and marks it completed.",
    changes: [
      "Teacher slot is marked used",
      "Class is bound to the teacher slot (and its booking slot) and marked completed",
      "Student class status is set to completed",
      "Student enrolment is assigned to the teacher",
    ],
    readyMessage: "Ready to assign the teacher and mark the class complete. Click Apply to proceed.",
    confirmTitle: "Complete this trial class?",
    notFoundHint:
      "Check the teacher ID, the date, the start time and the student's mobile number. The time " +
      "must match the class exactly, and the teacher must have a free slot then. If the slot is " +
      "already attached to the class, use Mark completed instead.",
  },
  "mark-completed": {
    label: "Mark completed",
    description:
      "The class already has the right teacher slot attached, but was never marked completed: " +
      "sets only the completion flags. Classes still in the future are refused.",
    changes: [
      "Class is marked completed (the teacher slot is left as it is)",
      "Student class status is set to completed",
    ],
    readyMessage: "Ready to mark the class complete. Click Apply to proceed.",
    confirmTitle: "Mark this trial class completed?",
    notFoundHint:
      "No class for this student is assigned to that teacher's slot at this date and time. If " +
      "no teacher slot is attached yet, use Complete class instead.",
  },
};

// <input type="time" step=1> gives "HH:MM" when the seconds are zero
const withSeconds = (time: string) => (time.length === 5 ? `${time}:00` : time);

const requestFrom = (form: FormState) => ({
  teacherId: form.teacherId.trim(),
  slotDate: form.slotDate,
  startTime: withSeconds(form.startTime),
  studentMobile: form.studentMobile.trim(),
});

// plain-language next steps for the answers an operator will actually see
const hintFor = (
  action: ClassCorrectionAction,
  status?: number,
  message = ""
): string | undefined => {
  switch (status) {
    case 401:
      return "Your login was not accepted. Sign out and sign in again, then retry.";
    case 403:
      return message.includes("ALLOW_WRITES")
        ? "Writes are switched off on this environment. Previews still work."
        : "Your account is not allowed to run class corrections. Ask for access.";
    case 404:
      return ACTIONS[action].notFoundHint;
    case 409:
      return "Nothing was changed. Read the message: the class may already be completed, may still be in the future, or more than one row matched.";
    case 422:
      return "Some fields are missing or in the wrong format.";
    case 429:
      return "Too many requests. Wait a minute and retry.";
    case 502:
    case 503:
      return "The class ops service could not verify the login or is not configured. Try again shortly, or tell the tech team.";
    default:
      return undefined;
  }
};

const outcomeFromError = (action: ClassCorrectionAction, error: any): Outcome => {
  const status: number | undefined = error?.response?.status;
  const body = error?.response?.data ?? {};
  // 422 arrives in FastAPI's validation shape, everything else in ours
  const message = Array.isArray(body.detail)
    ? body.detail.map((d: any) => `${(d.loc || []).slice(-1)[0]}: ${d.msg}`).join("; ")
    : body.error || (status ? "Request failed" : "Could not reach the class ops service");
  // a 409 carries the rows that made the request ambiguous
  const candidates: Record<string, (string | number)[]> = {};
  Object.entries(body).forEach(([key, value]) => {
    if (key !== "error" && key !== "detail" && Array.isArray(value)) {
      candidates[key] = value as (string | number)[];
    }
  });
  return {
    ok: false,
    title: status ? `Failed (HTTP ${status})` : "Request failed",
    message,
    hint: hintFor(action, status, message),
    candidates: Object.keys(candidates).length ? candidates : undefined,
  };
};

const ClassCorrection = () => {
  const [action, setAction] = useState<ClassCorrectionAction>("complete");
  const [form, setForm] = useState<FormState>(EMPTY);
  const [loading, setLoading] = useState<boolean>(false);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [confirming, setConfirming] = useState<boolean>(false);

  const configured = Boolean(CLASSOPS_BASEURL);

  const copy = ACTIONS[action];
  const currentKey = JSON.stringify({ action, ...requestFrom(form) });
  // apply is only offered for exactly what was previewed, with the same action
  const previewIsCurrent = preview !== null && preview.key === currentKey;

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setForm((previous) => ({ ...previous, [name]: value }));
    setOutcome(null);
  };

  const onActionChange = (next: ClassCorrectionAction) => {
    setAction(next);
    setOutcome(null);
  };

  const send = async (dryRun: boolean) => {
    setLoading(true);
    setOutcome(null);
    const key = currentKey;
    try {
      const response = await runClassCorrection(action, { ...requestFrom(form), dryRun });
      if (response.status === "dry_run") {
        setPreview({ key, resolved: response.resolved });
        setOutcome({
          ok: true,
          title: "Validation successful",
          message: copy.readyMessage,
        });
      } else {
        setPreview(null);
        setOutcome({
          ok: true,
          title: `${copy.label}: applied`,
          message: `Class ${response.resolved.class_schedule_id} is now completed.`,
        });
        setForm(EMPTY);
      }
    } catch (error: any) {
      if (!dryRun) setPreview(null);
      setOutcome(outcomeFromError(action, error));
    } finally {
      setLoading(false);
      setConfirming(false);
    }
  };

  const onPreview = (event: React.FormEvent) => {
    event.preventDefault();
    send(true);
  };

  const actionOptions = (Object.keys(ACTIONS) as ClassCorrectionAction[]).map((key) => ({ value: key, label: ACTIONS[key].label }));
  const closeConfirm = () => !loading && setConfirming(false);

  return (
    <Page>
      <PageHeader
        eyebrow="Teacher Operations"
        title="Class Correction"
        subtitle="Record the completion of a trial class that wasn't recorded. Preview first; nothing changes until you apply."
      />

      <Card className="class-correction-card">
        <div className="class-correction">
          <div className="class-correction__mode">
            <SegmentedControl label="Correction type" options={actionOptions} value={action} onChange={onActionChange} disabled={loading} />
            <p className="ds-text-muted class-correction__desc">{copy.description}</p>
          </div>

          {!configured && (
            <Callout tone="warning" title="Class correction isn't set up here">
              The service isn't configured for this environment (<code>REACT_APP_CLASSOPS_BASEURL</code> is empty).
            </Callout>
          )}

          <form className="class-correction__form" onSubmit={onPreview}>
            <Field label="Student mobile" id="studentMobile">
              {(ctl) => (
                <TextInput {...ctl} name="studentMobile" inputMode="numeric" required placeholder="7019817493" value={form.studentMobile} onChange={onChange} />
              )}
            </Field>
            <Field label="Class date" id="slotDate">
              {(ctl) => <TextInput {...ctl} name="slotDate" type="date" required value={form.slotDate} onChange={onChange} />}
            </Field>
            <Field label="Start time (IST)" id="startTime">
              {(ctl) => <TextInput {...ctl} name="startTime" type="time" step={1} required value={form.startTime} onChange={onChange} />}
            </Field>
            <Field label="Teacher ID" id="teacherId">
              {(ctl) => (
                <TextInput {...ctl} name="teacherId" inputMode="numeric" required placeholder="1247" value={form.teacherId} onChange={onChange} />
              )}
            </Field>

            <div className="class-correction__actions">
              <Button variant="secondary" type="submit" disabled={!configured} loading={loading && !confirming}>
                Preview
              </Button>
              <Button
                variant="danger"
                onClick={() => setConfirming(true)}
                disabled={loading || !previewIsCurrent}
                title={previewIsCurrent ? undefined : "Preview these exact details first"}
              >
                Apply
              </Button>
            </div>
            {preview && !previewIsCurrent && (
              <p className="class-correction__stale">The details changed since the preview. Preview again before applying.</p>
            )}
          </form>

          {outcome && (
            <Callout tone={outcome.ok ? "success" : "danger"} title={outcome.title}>
              <p>{outcome.message}</p>
              {outcome.hint && <p className="class-correction__hint">{outcome.hint}</p>}
              {outcome.candidates &&
                Object.entries(outcome.candidates).map(([key, values]) => (
                  <div key={key} className="class-correction__candidates">
                    <strong>{key}</strong>
                    <ul>
                      {values.map((value) => (
                        <li key={String(value)}><code>{value}</code></li>
                      ))}
                    </ul>
                  </div>
                ))}
            </Callout>
          )}

        </div>
      </Card>

      <Dialog
        open={confirming}
        onClose={closeConfirm}
        title={copy.confirmTitle}
        actions={
          <>
            <Button variant="secondary" onClick={() => setConfirming(false)} disabled={loading}>Cancel</Button>
            <Button variant="danger" onClick={() => send(false)} loading={loading}>Apply</Button>
          </>
        }
      >
        <p>
          Student <strong>{form.studentMobile}</strong>, class on <strong>{form.slotDate}</strong> at{" "}
          <strong>{withSeconds(form.startTime)}</strong>, teacher <strong>{form.teacherId}</strong>
          {preview && <> (class schedule <code>{preview.resolved.class_schedule_id}</code>)</>}.
        </p>
        <ul className="class-correction__changes">
          {copy.changes.map((change) => <li key={change}>{change}</li>)}
        </ul>
        <Callout tone="danger" title="This can't be undone." />
      </Dialog>
    </Page>
  );
};

export default ClassCorrection;
